import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import styles from "./ClientApproverSetting.module.css";
import { Approver, ClientApproverSettingProps } from "./ClientApproverSetting.utils";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Portal from '../Portal/Portal'

/**
 * ClientApproverSetting
 *
 * Displays the UI to select and manage approvers from a user list.
 * - Supports adding approvers via a dropdown.
 * - Prevents duplicates.
 * - Renders approver cards with basic info and removal option.
 */
const ClientApproverSetting: React.FC<ClientApproverSettingProps> = ({
  allUsers,
  selectedApprovers,
  approvalCheck,
  setSelectedApprovers,
  fieldsList = [],
  ruleFieldsList = [],
  coiApproverGroupLabel,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties | null>(null);
  const [searchText, setSearchText] = useState("");
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const [fieldDropdownStyles, setFieldDropdownStyles] = useState<React.CSSProperties | null>(null);
  const [fieldSearchText, setFieldSearchText] = useState("");
  const addFieldBtnRef = useRef<HTMLButtonElement>(null);

  const [showRuleDropdown, setShowRuleDropdown] = useState(false);
  const [ruleDropdownStyles, setRuleDropdownStyles] = useState<React.CSSProperties | null>(null);
  const [ruleSearchText, setRuleSearchText] = useState("");
  const addRuleBtnRef = useRef<HTMLButtonElement>(null);

  const activeApprovers = selectedApprovers.filter((a) => a.Active === "Y" || a.Active === true);
  const coiGroupApprovers = activeApprovers.filter((a) => a.COIApprover === "Y");
  const regularApprovers = activeApprovers.filter((a) => a.COIApprover !== "Y");

  const availableUsers = allUsers.filter(
    (user) => !selectedApprovers.some((a) => a.UserName === user.UserName && a.Active !== "N") && user.ShowInUI === "Y"
  );

  const filteredUsers = availableUsers.filter((user) =>
    `${user.FirstName} ${user.LastName} ${user.Email} ${user.Role}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  const availableFields = fieldsList.filter(
    (field) =>
      !selectedApprovers.some(
        (a) => a.ApproverType === "FIELD" && a.ApproverFieldCode === field.value && a.Active !== "N"
      )
  );

  const filteredFields = availableFields.filter((field) =>
    field.label.toLowerCase().includes(fieldSearchText.toLowerCase())
  );

  const availableRuleFields = ruleFieldsList.filter(
    (field) =>
      !selectedApprovers.some(
        (a) => a.ApproverType === "RULE" && a.ApproverFieldCode === field.value && a.Active !== "N"
      )
  );

  const filteredRuleFields = availableRuleFields.filter((field) =>
    field.label.toLowerCase().includes(ruleSearchText.toLowerCase())
  );

  const handleAddApprover = (user: Approver) => {
    const exists = selectedApprovers.find((a) => a.UserName === user.UserName);
    const updated = exists
      ? selectedApprovers.map((a) =>
          a.UserName === user.UserName ? { ...a, Active: "Y" } : a
        )
      : [...selectedApprovers, { ...user, Active: "Y" }];
    setSelectedApprovers(updateApproverOrder(updated));
    setShowDropdown(false);
    setSearchText("");
  };

  const handleAddFieldApprover = (field: { value: string; label: string }) => {
    const syntheticUserName = `field:${field.value}`;
    const exists = selectedApprovers.find((a) => a.UserName === syntheticUserName);
    const fieldApprover: Approver = {
      UserName: syntheticUserName,
      UserFullName: field.label,
      FirstName: field.label,
      LastName: "",
      FirmName: "",
      Email: "",
      Role: "Dynamic",
      ApprovalType: "",
      ApproverType: "FIELD",
      ApproverFieldCode: field.value,
      Active: "Y",
    };
    const updated = exists
      ? selectedApprovers.map((a) => (a.UserName === syntheticUserName ? { ...a, Active: "Y" } : a))
      : [...selectedApprovers, fieldApprover];
    setSelectedApprovers(updateApproverOrder(updated));
    setShowFieldDropdown(false);
    setFieldSearchText("");
  };

  const handleAddRuleApprover = (field: { value: string; label: string }) => {
    const syntheticUserName = `rule:${field.value}`;
    const exists = selectedApprovers.find((a) => a.UserName === syntheticUserName);
    const ruleApprover: Approver = {
      UserName: syntheticUserName,
      UserFullName: field.label,
      FirstName: field.label,
      LastName: "",
      FirmName: "",
      Email: "",
      Role: "Dynamic",
      ApprovalType: "",
      ApproverType: "RULE",
      ApproverFieldCode: field.value,
      Active: "Y",
    };
    const updated = exists
      ? selectedApprovers.map((a) => (a.UserName === syntheticUserName ? { ...a, Active: "Y" } : a))
      : [...selectedApprovers, ruleApprover];
    setSelectedApprovers(updateApproverOrder(updated));
    setShowRuleDropdown(false);
    setRuleSearchText("");
  };

  const updateApproverOrder = (approvers: Approver[]): Approver[] => {
    let activeOrder = 1;
    return approvers.map((approver) => {
      if (approver.Active === "Y") {
        return { ...approver, ApproverOrder: activeOrder++ };
      }
      return { ...approver, ApproverOrder: 0 };
    });
  };

  const openDropdown = () => {
    if (addBtnRef.current) {
      const rect = addBtnRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: 300,
        zIndex: 9999,
        background: "#fff",
        borderRadius: 5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 300,
        overflowY: "auto",
      });
      setShowDropdown(true);
    }
  };

  const closeDropdown = () => setShowDropdown(false);

  const openFieldDropdown = () => {
    if (addFieldBtnRef.current) {
      const rect = addFieldBtnRef.current.getBoundingClientRect();
      setFieldDropdownStyles({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: 300,
        zIndex: 9999,
        background: "#fff",
        borderRadius: 5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 300,
        overflowY: "auto",
      });
      setShowFieldDropdown(true);
    }
  };

  const closeFieldDropdown = () => setShowFieldDropdown(false);

  const openRuleDropdown = () => {
    if (addRuleBtnRef.current) {
      const rect = addRuleBtnRef.current.getBoundingClientRect();
      setRuleDropdownStyles({
        position: "fixed",
        top: rect.bottom + 5,
        left: rect.left,
        width: 300,
        zIndex: 9999,
        background: "#fff",
        borderRadius: 5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        maxHeight: 300,
        overflowY: "auto",
      });
      setShowRuleDropdown(true);
    }
  };

  const closeRuleDropdown = () => setShowRuleDropdown(false);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const dropdownEl = document.getElementById("approver-custom-dropdown");
      if (
        showDropdown &&
        !dropdownEl?.contains(e.target as Node) &&
        !addBtnRef.current?.contains(e.target as Node)
      ) {
        closeDropdown();
      }

      const fieldDropdownEl = document.getElementById("approver-field-custom-dropdown");
      if (
        showFieldDropdown &&
        !fieldDropdownEl?.contains(e.target as Node) &&
        !addFieldBtnRef.current?.contains(e.target as Node)
      ) {
        closeFieldDropdown();
      }

      const ruleDropdownEl = document.getElementById("approver-rule-custom-dropdown");
      if (
        showRuleDropdown &&
        !ruleDropdownEl?.contains(e.target as Node) &&
        !addRuleBtnRef.current?.contains(e.target as Node)
      ) {
        closeRuleDropdown();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showDropdown, showFieldDropdown, showRuleDropdown]);

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const coiApprovers = selectedApprovers.filter((a) => a.Active === "Y" && a.COIApprover === "Y");
    const regularApprovers = selectedApprovers.filter((a) => a.Active === "Y" && a.COIApprover !== "Y");
    const inactiveApprovers = selectedApprovers.filter((a) => a.Active !== "Y");

    const items = Array.from(regularApprovers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedApprovers(updateApproverOrder([...coiApprovers, ...items, ...inactiveApprovers]));
  };

  return (
    <>
      <div className={styles.approverHeaderRow}>
        <div className={styles.headerTitle}>Approvers</div>
        <div style={{ display: "flex", gap: "1rem" }}>
          <Button
            className={`${styles.addBtn} ${approvalCheck ? styles.disabled : ""}`}
            ref={addBtnRef}
            onClick={openDropdown}
            disabled={filteredUsers.length === 0}
          >
            <Icon.Plus
              className={`${styles.addBtnIcon} ${approvalCheck ? styles.disabled : ""}`}
            />{" "}
            Add approver
          </Button>
          {fieldsList.length > 0 && (
            <Button
              className={`${styles.addBtn} ${approvalCheck ? styles.disabled : ""}`}
              ref={addFieldBtnRef}
              onClick={openFieldDropdown}
              disabled={availableFields.length === 0}
            >
              <Icon.Plus
                className={`${styles.addBtnIcon} ${approvalCheck ? styles.disabled : ""}`}
              />{" "}
              Add approver by field
            </Button>
          )}
          {ruleFieldsList.length > 0 && (
            <Button
              className={`${styles.addBtn} ${approvalCheck ? styles.disabled : ""}`}
              ref={addRuleBtnRef}
              onClick={openRuleDropdown}
              disabled={availableRuleFields.length === 0}
            >
              <Icon.Plus
                className={`${styles.addBtnIcon} ${approvalCheck ? styles.disabled : ""}`}
              />{" "}
              Add approver by rule
            </Button>
          )}
        </div>
      </div>

      {showDropdown && dropdownStyles &&
        ReactDOM.createPortal(
          <div id="approver-custom-dropdown" style={dropdownStyles}>
            <div className={styles.dropdownSearchContainer}>
              <Icon.Search className={styles.searchIcon} />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search approver..."
                className={styles.dropdownSearch}
                defaultValue="Hello!"
                autoFocus
              />
            </div>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <button
                  key={user.UserName}
                  onClick={() => handleAddApprover(user)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontSize: 12,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  {user.UserFullName} - {user.Role}
                </button>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#777" }}>No approvers available</div>
            )}
          </div>,
          document.body
      )}

      {showFieldDropdown && fieldDropdownStyles &&
        ReactDOM.createPortal(
          <div id="approver-field-custom-dropdown" style={fieldDropdownStyles}>
            <div className={styles.dropdownSearchContainer}>
              <Icon.Search className={styles.searchIcon} />
              <input
                type="text"
                value={fieldSearchText}
                onChange={(e) => setFieldSearchText(e.target.value)}
                placeholder="Search field..."
                className={styles.dropdownSearch}
                autoFocus
              />
            </div>
            {filteredFields.length > 0 ? (
              filteredFields.map((field) => (
                <button
                  key={field.value}
                  onClick={() => handleAddFieldApprover(field)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontSize: 12,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  {field.label}
                </button>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#777" }}>No fields available</div>
            )}
          </div>,
          document.body
      )}

      {showRuleDropdown && ruleDropdownStyles &&
        ReactDOM.createPortal(
          <div id="approver-rule-custom-dropdown" style={ruleDropdownStyles}>
            <div className={styles.dropdownSearchContainer}>
              <Icon.Search className={styles.searchIcon} />
              <input
                type="text"
                value={ruleSearchText}
                onChange={(e) => setRuleSearchText(e.target.value)}
                placeholder="Search rule/privilege..."
                className={styles.dropdownSearch}
                autoFocus
              />
            </div>
            {filteredRuleFields.length > 0 ? (
              filteredRuleFields.map((field) => (
                <button
                  key={field.value}
                  onClick={() => handleAddRuleApprover(field)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontSize: 12,
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  {field.label}
                </button>
              ))
            ) : (
              <div style={{ padding: "8px 12px", color: "#777" }}>No rules available</div>
            )}
          </div>,
          document.body
      )}

      {activeApprovers.length === 0 && (
        <p className="text-danger">No approvers present in the document.</p>
      )}

      {coiGroupApprovers.length > 0 && (
        <div className={styles.approverList}>
          <div className={styles.approverCard}>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Tooltip id="coi-group-tooltip">
                  Resolved automatically from the COI Registry rule -- any one reviewer's
                  decision resolves this step for the whole group.
                </Tooltip>
              }
            >
              <Icon.LockFill size={16} className={styles.dragHandle} />
            </OverlayTrigger>

            <div className={`${styles.avatarCircle} ${styles.avatarCircleDynamic}`}>
              <Icon.ShieldFillCheck />
            </div>
            <div className={styles.approverDetails}>
              <div className={styles.approverName}>{coiApproverGroupLabel || "Reviewers"}</div>
              <div className={styles.meta}>
                <span className={styles.dynamicBadge}>By Rule</span>{" "}
                {coiGroupApprovers.length} reviewer{coiGroupApprovers.length > 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="approvers">
          {(provided) => (
            <div className={styles.approverList} {...provided.droppableProps} ref={provided.innerRef}>
              {regularApprovers.map((approver, index) => (
                  <Draggable
                    key={approver.UserName}
                    draggableId={approver.UserName}
                    index={index}
                    isDragDisabled={approvalCheck}
                  >
                    {(provided, snapshot) => {
                      const card = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${styles.approverCard} ${
                            snapshot.isDragging ? styles.dragging : ""
                          }`}
                        >
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={<Tooltip id="button-tooltip">Drag to reorder</Tooltip>}
                          >
                            <Icon.GripVertical
                              size={18}
                              className={`${styles.dragHandle} ${approvalCheck ? styles.disabled : ""}`}
                            />
                          </OverlayTrigger>

                          {approver.ApproverType === "FIELD" || approver.ApproverType === "RULE" ? (
                            <div className={`${styles.avatarCircle} ${styles.avatarCircleDynamic}`}>
                              {approver.ApproverType === "RULE" ? <Icon.ShieldFillCheck /> : <Icon.Braces />}
                            </div>
                          ) : (
                            <div className={styles.avatarCircle}>
                              {`${approver.FirstName?.[0] ?? ""}${approver.LastName?.[0] ?? ""}`.toUpperCase()}
                            </div>
                          )}
                          <div className={styles.approverDetails}>
                            <div className={styles.approverName}>
                              {approver.ApproverType === "FIELD"
                                ? fieldsList.find((f) => f.value === approver.ApproverFieldCode)?.label
                                  || approver.UserFullName
                                  || approver.ApproverFieldCode
                                : approver.ApproverType === "RULE"
                                  ? ruleFieldsList.find((f) => f.value === approver.ApproverFieldCode)?.label
                                    || approver.UserFullName
                                    || approver.ApproverFieldCode
                                  : approver.UserFullName
                                    || `${approver.FirstName ?? ""} ${approver.LastName ?? ""}`.trim()
                                    || approver.UserName}
                            </div>
                            {approver.ApproverType === "FIELD" || approver.ApproverType === "RULE" ? (
                              <div className={styles.meta}>
                                <span className={styles.dynamicBadge}>
                                  {approver.ApproverType === "RULE" ? "By Rule" : "Dynamic"}
                                </span>
                              </div>
                            ) : (
                              <>
                                <div className={styles.meta}>
                                  {approver.UserRole ?? approver.Role}{" "}
                                  {approver.FirmName ? " - " : ""} {approver.FirmName}
                                </div>
                                <div className={styles.emailHolder}>
                                  <a href={`mailto:${approver.UserName}`} className={styles.email}>
                                    {approver.UserName}
                                  </a>
                                </div>
                              </>
                            )}
                          </div>

                          {(() => {
                            const isLastActive =
                              activeApprovers.length === 1 &&
                              activeApprovers[0].UserName === approver.UserName;
                            const isDeleteDisabled =
                              (!approvalCheck && isLastActive) || approvalCheck;

                            return (
                              <Icon.Trash
                                className={`${styles.deleteIcon} ${
                                  isDeleteDisabled ? styles.disabled : ""
                                }`}
                                onClick={() => {
                                  if (isDeleteDisabled) return;
                                  setSelectedApprovers(
                                    updateApproverOrder(
                                      selectedApprovers.map((a) =>
                                        a.UserName === approver.UserName
                                          ? { ...a, Active: "N" }
                                          : a
                                      )
                                    )
                                  );
                                }}
                              />
                            );
                          })()}
                        </div>
                      );

                      return snapshot.isDragging ? <Portal>{card}</Portal> : card;
                    }}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default ClientApproverSetting;
